import { useState, useEffect } from 'react';
import { motion, useScroll, useSpring } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles, ShieldCheck, HeartPulse, Clock4, Star, CheckCircle2, Phone } from 'lucide-react';

const HomePage = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 100, damping: 30, restDelta: 0.001 });

  useEffect(() => {
    setIsLoaded(true);
    window.scrollTo(0, 0);
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.2, delayChildren: 0.3 }
    }
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] }
    }
  };

  return (
    <div className="relative bg-cream selection:bg-gold/30">
      {/* Scroll Progress Bar */}
      <motion.div
        className="fixed top-0 left-0 right-0 h-1 bg-gradient-gold z-[100] origin-left"
        style={{ scaleX }}
      />

      {/* --- HERO SECTION --- */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden bg-black">
        <div className="absolute inset-0 z-0">
          <motion.div
            initial={{ scale: 1.1, opacity: 0 }}
            animate={{ scale: 1, opacity: 0.6 }}
            transition={{ duration: 2, ease: "easeOut" }}
            className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/80 z-10"
          />

          {/* Surprise Background Blurs */}
          <div className="absolute top-1/4 -left-20 w-80 h-80 bg-sunset/20 rounded-full blur-[120px] animate-pulse"></div>
          <div className="absolute bottom-1/4 -right-20 w-96 h-96 bg-royal/20 rounded-full blur-[150px] animate-pulse delay-700"></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-rose/10 rounded-full blur-[200px] animate-pulse delay-1000"></div>

          <img
            src="/images/fppic.jpg"
            alt="Luxury African Braiding Charlotte"
            className="w-full h-full object-cover"
          />
        </div>

        <div className="relative z-20 text-center text-white px-4 max-w-7xl mx-auto mt-20">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate={isLoaded ? "visible" : "hidden"}
            className="space-y-6"
          >
            <motion.div variants={itemVariants} className="inline-flex items-center gap-2 px-6 py-2 rounded-full border border-white/10 bg-white/5 backdrop-blur-md shadow-royal">
              <Sparkles className="text-sunset w-4 h-4 animate-pulse" />
              <span className="text-white text-[10px] font-bold uppercase tracking-[0.3em] leading-none pt-1">The Gold Standard of Braiding</span>
              <Sparkles className="text-rose w-4 h-4 animate-pulse" />
            </motion.div>

            <motion.h1
              variants={itemVariants}
              className="text-7xl md:text-9xl lg:text-[10rem] font-serif font-bold tracking-tighter leading-[0.85] relative"
            >
              <div className="absolute -inset-10 bg-gradient-vibrant opacity-20 blur-[100px] animate-pulse-slow pointer-events-none"></div>
              <span className="text-gradient-gold block relative z-10 hover:skew-x-2 transition-transform duration-500 cursor-default">MOYHE</span>
              <span className="text-white block opacity-90 drop-shadow-2xl relative z-10 hover:-skew-x-2 transition-transform duration-500 cursor-default">ARTISTIC HAIR</span>
            </motion.h1>

            <motion.p variants={itemVariants} className="text-lg md:text-2xl font-light text-gray-300 max-w-3xl mx-auto leading-relaxed italic">
              "African Braiding Excellence in Charlotte. We focus on delivering the best services, treating you like the royalty you are. Where Tradition Meets Absolute Luxury and Mastery."
            </motion.p>

            <motion.div variants={itemVariants} className="flex flex-col sm:flex-row gap-6 justify-center pt-10">
              <a
                href="tel:17046417227"
                className="group relative bg-gradient-vibrant text-white px-12 py-5 rounded-full text-base font-bold transition-all duration-500 shadow-sunset hover:shadow-rose hover:scale-105 inline-flex items-center justify-center gap-3 overflow-hidden"
              >
                <span className="relative z-10 uppercase tracking-[0.2em] flex items-center gap-2"><Phone size={18} /> Call to Schedule</span>
                <div className="absolute inset-0 bg-white/20 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000"></div>
              </a>
              <a
                href="sms:17046417227"
                className="glass-dark text-white px-12 py-5 rounded-full text-base font-bold hover:bg-white/10 transition-all duration-500 inline-flex items-center justify-center gap-3 border border-white/10"
              >
                Text to Schedule <ArrowRight className="group-hover:translate-x-2 transition-transform duration-300 w-5 h-5" />
              </a>
            </motion.div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2.5, duration: 1.5 }}
          className="absolute bottom-12 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center gap-4"
        >
          <span className="text-[9px] uppercase tracking-[0.6em] text-white/40">Scroll to Immersion</span>
          <div className="w-[1px] h-20 bg-gradient-to-b from-gold to-transparent relative overflow-hidden">
            <motion.div
              animate={{ y: [0, 80] }}
              transition={{ repeat: Infinity, duration: 2, ease: "linear" }}
              className="absolute top-0 left-0 w-full h-1/2 bg-white"
            />
          </div>
        </motion.div>
      </section>

      {/* --- TRUST BANNER --- */}
      <section className="bg-dark py-10 overflow-hidden relative border-y border-white/5">
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-sunset/50 to-transparent"></div>
        <motion.div
          animate={{ x: [0, -1000] }}
          transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
          className="whitespace-nowrap flex gap-24 items-center relative z-10"
        >
          {[...Array(6)].map((_, i) => (
            <div key={i} className="flex items-center gap-8">
              <span className="text-white/90 font-serif text-lg md:text-xl flex items-center gap-4 italic font-light">
                <span className="text-2xl">⭐</span>
                Trusted by women across Charlotte for <span className="text-sunset font-bold uppercase tracking-[0.1em] not-italic px-2">neat</span>, <span className="text-rose font-bold uppercase tracking-[0.1em] not-italic px-2">gentle</span>, <span className="text-royal font-bold uppercase tracking-[0.1em] not-italic px-2">protective</span> styles
                <span className="text-2xl">⭐</span>
              </span>
              <div className="w-2 h-2 rounded-full bg-white/10"></div>
            </div>
          ))}
        </motion.div>
        <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-royal/50 to-transparent"></div>
      </section>

      {/* --- PHILOSOPHY SECTION --- */}
      <section className="py-40 px-4 bg-white relative">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-24 items-center">
            <motion.div
              initial={{ opacity: 0, x: -60 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 1.2 }}
              className="space-y-12"
            >
              <div className="space-y-4">
                <p className="text-gold font-bold uppercase tracking-[0.4em] text-xs">Our Heritage</p>
                <h2 className="text-6xl md:text-8xl font-serif font-bold text-dark leading-tight tracking-tighter">
                  Elegance in <br /><span className="text-gold italic">Every Detail</span>
                </h2>
              </div>

              <p className="text-2xl text-gray-500 leading-relaxed font-light max-w-xl italic">
                "For 30 years, we've treated braiding not just as a service, but as an art form. Your hair is our canvas."
              </p>

              <div className="grid sm:grid-cols-2 gap-10">
                {[
                  { icon: <Clock4 />, title: "Concierge Timing", desc: "No queues. Only punctuality." },
                  { icon: <ShieldCheck />, title: "Premium Care", desc: "Scalp-first philosophy." },
                  { icon: <HeartPulse />, title: "Bespoke Styles", desc: "Tailored to your features." },
                  { icon: <CheckCircle2 />, title: "Elite Durability", desc: "Stays fresh for 8+ weeks." }
                ].map((item, i) => (
                  <motion.div
                    key={i}
                    whileHover={{ scale: 1.05, x: 10 }}
                    transition={{ type: "spring", stiffness: 300 }}
                    className="flex items-start gap-4 p-4 rounded-2xl hover:bg-gold/5 transition-colors border border-transparent hover:border-gold/10"
                  >
                    <div className="p-3 rounded-xl bg-gold/10 text-gold shadow-sm">
                      {item.icon}
                    </div>
                    <div>
                      <h3 className="font-bold text-lg text-dark tracking-tight">{item.title}</h3>
                      <p className="text-gray-400 text-sm font-medium">{item.desc}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Floating Elements for "Crazy" Effect */}
            <div className="absolute top-0 right-0 -z-10 w-64 h-64 bg-royal/10 rounded-full blur-[80px] animate-float"></div>
            <div className="absolute bottom-0 left-0 -z-10 w-64 h-64 bg-rose/10 rounded-full blur-[80px] animate-float" style={{ animationDelay: '2s' }}></div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9, rotate: -2 }}
              whileInView={{ opacity: 1, scale: 1, rotate: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 1.5, ease: [0.22, 1, 0.36, 1] }}
              className="relative"
            >
              <div className="absolute inset-0 bg-gold/5 rounded-[40px] translate-x-6 translate-y-6 -z-10 border border-gold/10"></div>
              <img
                src="/images/bohobox.jpg"
                alt="Elite Braiding Service"
                className="relative rounded-[40px] shadow-premium z-10 w-full hover:scale-[1.03] transition-transform duration-1000 ease-out"
              />
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.5 }}
                className="absolute -bottom-10 -left-10 glass p-10 rounded-[30px] shadow-[0_30px_60px_rgba(0,0,0,0.1)] z-20 border border-white/50"
              >
                <div className="space-y-1">
                  <p className="text-gold font-serif text-5xl font-bold italic tracking-tighter">30+</p>
                  <p className="text-[10px] uppercase tracking-[0.3em] font-black text-dark/70">Years of Mastery</p>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* --- COLLECTION STRIP --- */}
      <section className="py-32 bg-dark overflow-hidden relative">
        <div className="absolute top-0 left-0 w-full h-full opacity-5 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')]"></div>
        <div className="max-w-7xl mx-auto px-4 relative z-10 text-center mb-24">
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            className="text-gold font-bold uppercase tracking-[0.6em] text-[10px] mb-6"
          >
            Curated Styles
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            className="text-5xl md:text-8xl font-serif font-bold text-white tracking-tighter"
          >
            The <span className="text-gold italic">Couture</span> Line
          </motion.h2>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-1 px-1">
          {[
            { title: "Conrows", img: "/images/conrows12.jpg", tag: "Signature" },
            { title: "Senegalese Braids", img: "/images/seneg.jpg", tag: "Elegance" },
            { title: "Comb Braids", img: "/images/combraid.jpg", tag: "Heritage" }
          ].map((item, i) => (
            <motion.div
              key={i}
              whileHover={{ scale: 0.98 }}
              className="relative h-[650px] group overflow-hidden cursor-pointer"
            >
              <img src={item.img} alt={item.title} className="w-full h-full object-cover transition-transform duration-[2s] group-hover:scale-125" />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent opacity-60 group-hover:opacity-90 transition-opacity duration-700"></div>
              <div className="absolute inset-0 p-16 flex flex-col justify-end transform transition-transform duration-700 group-hover:-translate-y-4">
                <span className="text-gold font-bold uppercase tracking-[0.4em] text-[9px] mb-4 bg-gold/10 self-start px-3 py-1 rounded-full border border-gold/20">{item.tag}</span>
                <h3 className="text-4xl font-serif font-bold text-white mb-6 tracking-tight">{item.title}</h3>
                <div className="h-[1px] w-0 group-hover:w-full bg-gold transition-all duration-700"></div>
                <Link to="/services" className="text-white mt-6 flex items-center gap-3 font-bold uppercase tracking-widest text-xs opacity-0 group-hover:opacity-100 transition-opacity duration-1000">
                  Enter Collection <ArrowRight size={14} className="text-gold" />
                </Link>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Parallax Background Movement */}
        <motion.div
          className="absolute inset-0 bg-gradient-vibrant opacity-5 -z-10"
          style={{ y: useSpring(useScroll().scrollYProgress, { stiffness: 50, damping: 20 }) }}
        />
      </section>


      {/* --- CONSULTATION SECTION --- */}
      <section className="py-40 bg-white relative overflow-hidden">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-gold/5 rounded-full blur-[100px]"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gold/5 rounded-full blur-[100px]"></div>

        <div className="max-w-5xl mx-auto px-4 text-center mb-24">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            className="inline-block p-4 rounded-3xl bg-gold/5 mb-10"
          >
            <Star className="text-gold w-10 h-10 fill-gold" />
          </motion.div>
          <h2 className="text-5xl md:text-8xl font-serif font-bold text-dark tracking-tighter mb-8 leading-none">
            Join the <span className="text-gold">Elite List</span>
          </h2>
          <p className="text-xl text-gray-500 max-w-2xl mx-auto leading-relaxed font-light italic">
            Experience the pinnacle of hair care. Our spots fill up quickly due to high demand.
          </p>
        </div>

        <div className="max-w-4xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            className="p-12 md:p-20 rounded-[50px] bg-dark shadow-3xl text-white relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-full h-full bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-[0.03]"></div>

            <div className="text-center z-10 relative">
              <div className="flex flex-col items-center gap-8 mb-12">
                <a href="tel:17046417227" className="group">
                  <div className="bg-gradient-gold p-8 rounded-full shadow-[0_0_50px_rgba(212,175,55,0.4)] group-hover:scale-110 transition-transform duration-500">
                    <Phone size={48} className="text-dark" />
                  </div>
                </a>
                <div>
                  <p className="text-gold font-bold uppercase tracking-[0.3em] mb-2">Direct Concierge Line</p>
                  <a href="tel:17046417227" className="text-4xl md:text-6xl font-serif font-bold text-white hover:text-gold transition-colors">
                    (704) 641-7227
                  </a>
                </div>
              </div>

              <div className="flex flex-col md:flex-row justify-center gap-6">
                <a
                  href="tel:17046417227"
                  className="bg-white text-dark px-10 py-4 rounded-full font-bold uppercase tracking-widest hover:bg-gold transition-colors duration-300"
                >
                  Call Now
                </a>
                <a
                  href="sms:17046417227"
                  className="border border-white/30 text-white px-10 py-4 rounded-full font-bold uppercase tracking-widest hover:bg-white/10 transition-colors duration-300"
                >
                  Send SMS
                </a>
              </div>
              <p className="mt-8 text-white/40 text-sm">Consultations typically require 48h notice.</p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
